import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ban, AlertTriangle, Clock, CalendarClock, ArrowRight, Store, Receipt,
  BarChart3, Wallet, ListChecks, Info, FileText, ReceiptText, Banknote,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import SyncBar from '../components/SyncBar';
import PetanqueLoader from '../components/PetanqueLoader';
import { Badge } from '../components/ui/badge';
import { api } from '../api/sangly';
import { money, formatNumber, fmtDate, BLOCKED } from '../lib/format';
import { cn } from '../lib/utils';
import usePollWhileActive from '../hooks/usePollWhileActive';

const ROSE = { bg: 'bg-rose-500/12', fg: 'text-rose-600' };
const AMBER = { bg: 'bg-amber-500/12', fg: 'text-amber-600' };

function ActionCard({ tone, icon: Icon, title, amount, count, unit, action, onClick }) {
  return (
    <button onClick={onClick} title={action}
      className="group flex flex-col rounded-lg border border-border bg-card p-3 text-left shadow-card transition hover:border-primary/40 hover:shadow-pop">
      <div className="flex items-center gap-1.5">
        <span className={cn('grid h-6 w-6 shrink-0 place-items-center rounded-md', tone.bg)}>
          <Icon className={cn('h-3.5 w-3.5', tone.fg)} />
        </span>
        <span className="text-[12px] font-medium">{title}</span>
        <Info className="ml-auto h-3.5 w-3.5 text-muted-foreground/50" title={action} />
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={cn('text-xl font-semibold tnum', tone.fg)}>{money(amount)}</span>
        <span className="text-[11px] text-muted-foreground">{formatNumber(count)} {unit}</span>
      </div>
    </button>
  );
}

// The three "money to send" queues - pro-formas, expense notes, refunds - share
// one card: header with a count, the total, a few of the most urgent rows, and
// a "View all" into the page that works the queue. Calm when empty.
const TONES = {
  sky: { box: 'bg-sky-500/15 text-sky-600', border: 'border-sky-200 bg-sky-500/[0.05]', link: 'text-sky-700' },
  amber: { box: 'bg-amber-500/15 text-amber-600', border: 'border-amber-200 bg-amber-500/[0.05]', link: 'text-amber-700' },
  violet: { box: 'bg-violet-500/15 text-violet-600', border: 'border-violet-200 bg-violet-500/[0.05]', link: 'text-violet-700' },
};

function QueueCard({ icon: Icon, title, tone, count, total, badges = [], items = [], more = 0, empty, onOpen, onItem }) {
  const t = TONES[tone] || TONES.sky;
  const active = count > 0;
  return (
    <section className={cn('flex flex-col rounded-xl border p-4 shadow-card', active ? t.border : 'border-border bg-card')}>
      <div className="flex items-center gap-2">
        <span className={cn('grid h-6 w-6 shrink-0 place-items-center rounded-md', active ? t.box : 'bg-muted text-muted-foreground')}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h2 className="text-[13px] font-semibold">{title}</h2>
        <Badge variant={active ? 'info' : 'default'}>{formatNumber(count)}</Badge>
        <button onClick={onOpen} className={cn('ml-auto inline-flex items-center gap-1 text-[12px] font-medium hover:underline', active ? t.link : 'text-muted-foreground')}>
          View all <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      {active ? (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-[18px] font-semibold tnum">{total}</span>
            {badges.map((b) => <Badge key={b.label} variant={b.variant}>{b.label}</Badge>)}
          </div>
          <ol className="mt-2 divide-y divide-border/70">
            {items.map((it) => (
              <li key={it.key}>
                <button onClick={() => onItem?.(it)} className="flex w-full items-center gap-2 py-1.5 text-left transition-colors hover:bg-muted/40">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{it.label}</div>
                    {it.sub && <div className="truncate text-[11px] text-muted-foreground">{it.sub}</div>}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[13px] font-semibold tnum">{it.amount}</div>
                    {it.hint && <div className={cn('text-[11px]', it.hintTone || 'text-muted-foreground')}>{it.hint}</div>}
                  </div>
                </button>
              </li>
            ))}
          </ol>
          {more > 0 && (
            <button onClick={onOpen} className={cn('mt-1 self-start text-[12px] font-medium hover:underline', t.link)}>+{formatNumber(more)} more</button>
          )}
        </>
      ) : (
        <p className="mt-2 text-[12px] text-emerald-600">{empty}</p>
      )}
    </section>
  );
}

const totalsLabel = (totals) => (totals || []).map((x) => money(x.amount, x.currency)).join(' + ');

function ProformaAlert({ data, navigate }) {
  const items = (data?.items || []).slice(0, 4).map((p) => ({
    key: p.id, id: p.id, label: p.supplier_name || '-', sub: p.reference,
    amount: p.amount > 0 ? money(p.amount, p.currency) : 'amount?',
    hint: p.in_payment ? 'in payment' : p.due_date ? `${p.late ? 'late' : 'by'} ${fmtDate(p.due_date)}` : '',
    hintTone: p.in_payment ? 'text-primary' : p.late ? 'font-medium text-rose-600' : '',
  }));
  const badges = [];
  if (data?.late_count) badges.push({ label: `${data.late_count} past deadline`, variant: 'destructive' });
  if (data?.in_payment_count) badges.push({ label: `${data.in_payment_count} in payment`, variant: 'accent' });
  return (
    <QueueCard icon={FileText} title="Pro-forma to pay" tone="sky" count={data?.count || 0}
      total={totalsLabel(data?.totals)} badges={badges} items={items} more={(data?.count || 0) - items.length}
      empty="Nothing to pay — pro-formas appear here once logged."
      onOpen={() => navigate('/proformas')} onItem={(it) => navigate(`/proformas/${it.id}`)} />
  );
}

// Employee expense notes (NDF-…) still someone's to-do: to reimburse, to post
// in Odoo, or waiting in Odoo Documents with no vendor bill yet. Same count as
// the nav badge, so the two never disagree.
const NOTE_HINT = { waiting: 'waiting in Odoo', draft: 'draft to post' };
function ExpenseNotesCard({ data, navigate }) {
  const pending = data?.pending_count || 0;
  const items = (data?.items || []).slice(0, 4).map((i) => ({
    key: i.ref || i.id, id: i.id, label: i.employee || i.supplier || '-', sub: i.ref,
    amount: i.amount == null ? 'amount?' : money(i.amount, i.currency),
    hint: NOTE_HINT[i.kind] || (i.date_due ? `${i.late ? 'late' : 'due'} ${fmtDate(i.date_due)}` : ''),
    hintTone: i.late ? 'font-medium text-rose-600' : i.kind === 'waiting' ? 'text-sky-700' : '',
  }));
  const late = data?.late_count || 0;
  const waiting = data?.waiting_count || 0;
  const drafts = data?.draft_count || 0;   // still unposted in Odoo: not payable yet
  const badges = [
    ...(late ? [{ label: `${late} overdue`, variant: 'destructive' }] : []),
    ...(waiting ? [{ label: `${waiting} waiting in Odoo`, variant: 'info' }] : []),
    ...(drafts ? [{ label: `${drafts} draft${drafts > 1 ? 's' : ''} to post`, variant: 'warning' }] : []),
  ];
  return (
    <QueueCard icon={ReceiptText} title="Expense notes to reimburse" tone="amber" count={pending}
      total={money(data?.pending_amount || 0)} badges={badges}
      items={items} more={pending - items.length}
      empty={`All reimbursed · ${formatNumber(data?.total_count || 0)} notes in total`}
      onOpen={() => navigate('/expense-notes')}
      onItem={(it) => navigate(it.id ? `/expense-notes/${it.id}` : '/expense-notes')} />
  );
}

// Customer refunds to deposit by bank transfer (Refund Mgmt's pending rows).
function RefundsCard({ data, navigate }) {
  const items = (data?.items || []).slice(0, 4).map((r) => ({
    key: r.id, id: r.id, label: r.client || '-', sub: r.credit_note_ref,
    amount: money(r.amount, r.currency),
    hint: r.added ? `added ${fmtDate(r.added)}` : '',
  }));
  return (
    <QueueCard icon={Banknote} title="Refunds to deposit" tone="violet" count={data?.count || 0}
      total={totalsLabel(data?.totals)} items={items} more={(data?.count || 0) - items.length}
      badges={data?.to_finalize_count ? [{ label: `${data.to_finalize_count} to finalize`, variant: 'info' }] : []}
      empty="No refunds waiting."
      onOpen={() => navigate('/refunds')} onItem={() => navigate('/refunds')} />
  );
}

function TopColumn({ title, items, navigate }) {
  return (
    <div>
      <div className="mb-1 text-[12px] font-semibold text-muted-foreground">{title}</div>
      <ol className="divide-y divide-border">
        {items.length === 0 && <li className="py-3 text-[12px] text-muted-foreground">Nothing overdue here.</li>}
        {items.map((s, i) => {
          const blk = BLOCKED[s.account_status] || BLOCKED[1];
          return (
            <li key={s.id}>
              <button onClick={() => navigate(`/suppliers/${s.id}`)}
                className="flex w-full items-center gap-2.5 py-2 text-left transition-colors hover:bg-muted/40">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[13px] font-medium">{s.name || '-'}</span>
                    {s.account_status > 1 && <Badge variant={blk.variant} className="text-[10px]">{blk.label}</Badge>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {formatNumber(s.overdue_count)} overdue{s.over90_amount > 0 ? ` · ${money(s.over90_amount)} over 90d` : ''}
                  </div>
                </div>
                <span className="shrink-0 text-[13px] font-semibold tnum text-rose-600">{money(s.overdue_amount)}</span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function BandChart({ icon: Icon, title, subtitle, rows, aFg, urgentLabel, navigate, to }) {
  const max = Math.max(1, ...rows.map((r) => r.sourced + r.other));
  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="text-[14px] font-semibold">{title}</h2>
        <span className="text-[12px] text-muted-foreground">{subtitle}</span>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className={cn('h-2.5 w-2.5 rounded-sm', aFg)} /> Sourced</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-slate-400" /> Other</span>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const total = r.sourced + r.other;
          const w = (total / max) * 100;
          const sPct = total ? (r.sourced / total) * 100 : 0;
          const urgent = urgentLabel && r.label.startsWith(urgentLabel);
          return (
            <button key={r.label} onClick={() => navigate(to)} className="flex w-full items-center gap-3 text-left">
              <div className="w-24 shrink-0">
                <div className={cn('text-[12px] font-medium', urgent && 'text-rose-600')}>{r.label}</div>
                <div className="text-[10px] text-muted-foreground">{formatNumber(r.count)} bills</div>
              </div>
              <div className="h-5 flex-1 overflow-hidden rounded bg-muted/40">
                <div className="flex h-full min-w-[2px]" style={{ width: `${Math.max(w, 1.5)}%` }} title={`${money(total)}`}>
                  <div className={cn('h-full', aFg)} style={{ width: `${sPct}%` }} />
                  <div className="h-full bg-slate-400" style={{ width: `${100 - sPct}%` }} />
                </div>
              </div>
              <div className="w-24 shrink-0 text-right text-[12px] font-semibold tnum">{money(total)}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Section({ icon: Icon, title, subtitle, data, sourced, navigate }) {
  const type = sourced ? 'supplier' : 'other';
  return (
    <section className="mb-5">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border pb-1.5">
        <span className="grid h-6 w-6 place-items-center rounded-md bg-primary/12 text-primary"><Icon className="h-3.5 w-3.5" /></span>
        <h2 className="text-[14px] font-semibold">{title}</h2>
        <span className="text-[12px] text-muted-foreground">{subtitle}</span>
        <span className="ml-auto text-[12px]">
          <span className="font-semibold tnum text-rose-600">{money(data.overdue_amount)}</span>
          <span className="text-muted-foreground"> overdue · {money(data.open_amount)} open</span>
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
        <ActionCard tone={ROSE} icon={Ban} title="Blocked" amount={data.blocked_amount} count={data.blocked_count} unit="vendors"
          action="Vendors that have blocked our account. Settle these to get unblocked."
          onClick={() => navigate(`/suppliers?filter=blocked&sourced=${sourced}`)} />
        <ActionCard tone={AMBER} icon={AlertTriangle} title="At-risk" amount={data.at_risk_amount} count={data.at_risk_count} unit="vendors"
          action="Vendors close to blocking us. Pay soon to stay in good standing."
          onClick={() => navigate(`/suppliers?filter=at_risk&sourced=${sourced}`)} />
        <ActionCard tone={ROSE} icon={Clock} title="Overdue" amount={data.overdue_amount} count={data.overdue_count} unit="invoices"
          action="Bills already past their due date. Pay now, biggest and oldest first."
          onClick={() => navigate(`/invoices?filter=overdue&type=${type}`)} />
        <ActionCard tone={AMBER} icon={CalendarClock} title="Due 7d" amount={data.due_amount} count={data.due_count} unit="invoices"
          action="Bills due within the next 7 days. Pay this week to avoid going overdue."
          onClick={() => navigate(`/invoices?filter=due_week&type=${type}`)} />
      </div>
    </section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [top, setTop] = useState({ sourced: [], other: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const [s, ts, to] = await Promise.all([
        // the dashboard sits in the LampTwist block; Calum has its own pages
        api.invoices.stats({ unit: 'lamptwist' }), api.suppliers.top(5, 'supplier', 'lamptwist'), api.suppliers.top(5, 'other', 'lamptwist'),
      ]);
      setStats(s);
      setTop({ sourced: ts || [], other: to || [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  usePollWhileActive(true, () => load(true), 30000);

  const beh = stats?.pay_behaviour;

  return (
    <AppLayout
      title="Action dashboard"
      description="What to pay - split by sourced vendors and other invoices"
      syncBar={<SyncBar onSynced={() => load(true)} />}
    >
      {loading || !stats ? (
        <div className="grid h-64 place-items-center"><PetanqueLoader size={38} roll /></div>
      ) : (
        <>
          <div className="mb-5 rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <div>
                <div className="text-[12px] uppercase tracking-wide text-muted-foreground">Overdue to pay</div>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-3xl font-semibold tnum text-rose-600">{money(stats.overdue_amount)}</span>
                  <span className="text-[14px] text-muted-foreground">
                    across {formatNumber(stats.overdue_count)} invoices · {money(stats.open_amount)} open in total
                  </span>
                </div>
              </div>
              {beh?.paid_count > 0 && (
                <div className="text-right text-[13px]">
                  <div className={cn('font-semibold', beh.avg_delay_days <= 2 ? 'text-emerald-600' : beh.avg_delay_days <= 10 ? 'text-amber-600' : 'text-rose-600')}>
                    {beh.avg_delay_days <= -2 ? `We pay ~${Math.abs(Math.round(beh.avg_delay_days))}d early`
                      : beh.avg_delay_days <= 2 ? 'We pay on time' : `We pay ~${Math.round(beh.avg_delay_days)}d late`}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{beh.on_time_rate}% on time · {formatNumber(beh.paid_count)} paid</div>
                </div>
              )}
            </div>
          </div>

          {/* the three "money to send" queues, side by side */}
          <div className="mb-5 grid gap-4 md:grid-cols-3">
            <ProformaAlert data={stats.proforma} navigate={navigate} />
            <ExpenseNotesCard data={stats.expense_notes} navigate={navigate} />
            <RefundsCard data={stats.refunds} navigate={navigate} />
          </div>

          <section className="mb-5 rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />
              <h2 className="text-[14px] font-semibold">Top priorities to pay</h2>
              <span className="text-[12px] text-muted-foreground">ranked by value and how overdue</span>
            </div>
            <div className="grid gap-x-6 gap-y-1 lg:grid-cols-2">
              <TopColumn title="Sourced vendors (PO)" items={top.sourced} navigate={navigate} />
              <TopColumn title="Other invoices" items={top.other} navigate={navigate} />
            </div>
          </section>

          <div className="mb-5 grid gap-4 xl:grid-cols-2">
            {stats.aging && (
              <BandChart icon={BarChart3} title="Aging of overdue" subtitle="oldest first" aFg="bg-rose-500"
                urgentLabel="90" rows={stats.aging} navigate={navigate} to="/invoices?filter=overdue" />
            )}
            {stats.forecast && (
              <BandChart icon={Wallet} title="Upcoming to pay" subtitle="not yet overdue" aFg="bg-amber-500"
                rows={stats.forecast} navigate={navigate} to="/invoices?filter=open" />
            )}
          </div>

          <Section icon={Store} title="Sourced vendors" subtitle="Brands we place purchase orders with"
            navigate={navigate} data={stats.by_type.sourced} sourced />
          <Section icon={Receipt} title="Other invoices" subtitle="Expenses & one-off vendors (no PO)"
            navigate={navigate} data={stats.by_type.other} sourced={false} />
        </>
      )}
    </AppLayout>
  );
}
